import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingController } from '@ionic/angular';
import { CacheService } from './cache.service';
import { switchMap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { from, Observable, of, throwError, zip } from 'rxjs';
import { LoginReq } from '../models/login/loginReq';
import { LoginRes } from '../models/login/loginRes';
import { environment } from '../../environments/environment';
import { Device } from '@capacitor/device';
import { CalendarRes } from '../models/calendar/calendarRes';
import { OrderRes } from '../models/order/orderRes';

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  users = null;
  loginUrl = `${environment.apiUrl}/LoginServlet`;
  isEditableBe: boolean;

  constructor(
    private activatedRoute: ActivatedRoute,
    private loadingController: LoadingController,
    private cacheService: CacheService,
    private http: HttpClient,
    private router: Router
  ) {
  }

  getAuthData(loginName, loginPassword): Observable<LoginRes | null> {
    return zip(
      from(Device.getInfo()),
      from(Device.getId())
    )
      .pipe(
        switchMap(deviceInfo => {
          const login: LoginReq = {
            loginName,
            loginPassword,
            notificationToken: localStorage.getItem('notificationToken'),
            deviceToken: '',
            appName: 'RISPETTO_APP',
            deviceName: deviceInfo[0].name + ' uuid: ' + deviceInfo[1].uuid,
            deviceOs: deviceInfo[0].operatingSystem + ' ' + deviceInfo[0].osVersion
          };

          return this.http.post(this.loginUrl, login
          ).pipe(
            switchMap(result => {
              if (!result) {
                return of(null);
              } else {
                login.loginPassword = '';
                this.cacheService.cacheRequests(this.loginUrl, result, 1000000000).subscribe();
                return of(result);
              }
            })
          );
        })
      );
  }

  deleteAuthData(): Observable<any> {
    this.router.navigate(['/login']);
    this.cacheService.clearCacheData();
    return this.cacheService.invalidateCacheEntry(this.loginUrl);
  }

  isLoggedIn(): Observable<boolean> {
    return this.cacheService.getCachedRequest(this.loginUrl).pipe(
      switchMap(result => {
        if (result?.data?.token) {
          return of(true);
        } else {
          return of(false);
        }
      })
    );
  }

  getToken() {
    return this.cacheService.getCachedRequest(this.loginUrl).pipe(
      switchMap(result => {
        if (result?.data?.token) {
          return of(result.data.token);
        } else {
          return of(null);
        }
      }),
      switchMap(token => {
        if (!token) {
          return throwError({
            msg: 'Unauthorized',
            status: 401
          });
        } else {
          return of(token);
        }
      }));
  }

  getIntrauser() {
    return this.cacheService.getCachedRequest(this.loginUrl).pipe(
      switchMap(result => {
        if (result?.data?.token) {
          return of(result.data);
        } else {
          return of(null);
        }
      }));
  }

  getIntrauserId() {
    return this.cacheService.getCachedRequest(this.loginUrl).pipe(
      switchMap(result => {
        if (result?.data?.token) {
          return of(result.data.intrauser_id);
        } else {
          return of(null);
        }
      }));
  }

  setSelectedSubjectId(subjectId) {
    return this.cacheService.cacheRequests('selectedSubject', subjectId, 1000000000);
  }

  getSelectedSubjectId() {
    return this.cacheService.getCachedRequest('selectedSubject').pipe(
      switchMap(result => {
        if (result?.data) {
          return of(result.data);
        } else {
          return of(null);
        }
      }));
  }

  isEditable(calendar?: CalendarRes): Observable<boolean> {
    return zip(
      this.getSelectedSubjectId(),
      this.getIntrauserId(),
      from(Device.getInfo())
    ).pipe(
      switchMap(ids => {
        if (Number(ids[0]) === Number(ids[1])) {
          if (calendar) {
            const dateParts = calendar.date.split('.');
            const calendarDate = new Date(Number(dateParts[2]), Number(dateParts[1]) - 1, Number(dateParts[0]));
            const actDate = new Date();
            actDate.setHours(0, 0, 0, 0);
            actDate.setDate(actDate.getDate() - 30);

            if (actDate > calendarDate) {
              return of(false);
            }
            return of(true);
          } else {
            return of(true);
          }
        } else {
          return of(false);
        }
      })
    );
  }

  isEditableOrders(calendar?: CalendarRes, order?: OrderRes): Observable<boolean> {
    return zip(
      this.getSelectedSubjectId(),
      this.getIntrauserId(),
      from(Device.getInfo())
    ).pipe(
      switchMap(ids => {
        if (Number(ids[0]) === Number(ids[1])) {
          const referenceDate = this.getOrderEditableReferenceDate(order, calendar);
          if (referenceDate) {
            const actDate = new Date();
            actDate.setHours(0, 0, 0, 0);
            actDate.setDate(actDate.getDate() - 30);

            if (actDate > referenceDate) {
              return of(false);
            }
            return of(true);
          } else {
            return of(true);
          }
        } else {
          return of(false);
        }
      })
    );
  }

  private getOrderEditableReferenceDate(order?: OrderRes, calendar?: CalendarRes): Date | null {
    return this.parseEditableDate(order?.date) ?? this.parseEditableDate(calendar?.date);
  }

  private parseEditableDate(dateValue?: string): Date | null {
    if (!dateValue) {
      return null;
    }

    const normalizedDate = dateValue.trim();
    if (!normalizedDate) {
      return null;
    }

    if (normalizedDate.includes('.')) {
      const dateParts = normalizedDate.split('.');
      if (dateParts.length >= 3) {
        const parsedDate = new Date(Number(dateParts[2]), Number(dateParts[1]) - 1, Number(dateParts[0]));
        parsedDate.setHours(0, 0, 0, 0);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
      }
    }

    const parsedDate = new Date(normalizedDate);
    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    parsedDate.setHours(0, 0, 0, 0);
    return parsedDate;
  }

  isEditableDocuments(calendar?: CalendarRes): Observable<boolean> {
    return zip(
      this.getSelectedSubjectId(),
      this.getIntrauserId(),
      from(Device.getInfo())
    ).pipe(
      switchMap(ids => {
        if (Number(ids[0]) === Number(ids[1])
          && (ids[2].model === 'SM-P610' || ids[2].model === 'SM-P615' || ids[2].platform === 'web')) {
          if (calendar) {
            const dateParts = calendar.date.split('.');
            const calendarDate = new Date(Number(dateParts[2]), Number(dateParts[1]) - 1, Number(dateParts[0]));
            const actDate = new Date();
            actDate.setHours(0, 0, 0, 0);
            actDate.setDate(actDate.getDate() - 90);

            if (actDate > calendarDate) {
              return of(false);
            }
            return of(true);
          } else {
            return of(true);
          }
        } else {
          return of(false);
        }
      })
    );
  }

  isEditableRecommendations(calendar?: CalendarRes): Observable<boolean> {
    return zip(
      this.getSelectedSubjectId(),
      this.getIntrauserId(),
      from(Device.getInfo())
    ).pipe(
      switchMap(ids => {
        if (Number(ids[0]) === Number(ids[1])
          && (ids[2].model === 'SM-P610' || ids[2].model === 'SM-P615' || ids[2].platform === 'web')) {
          if (calendar) {
            const dateParts = calendar.date.split('.');
            const calendarDate = new Date(Number(dateParts[2]), Number(dateParts[1]) - 1, Number(dateParts[0]));
            const actDate = new Date();
            actDate.setHours(0, 0, 0, 0);
            actDate.setDate(actDate.getDate() - 30);
            if (actDate > calendarDate) {
              return of(false);
            }
            return of(true);
          } else {
            return of(true);
          }
        } else {
          return of(false);
        }
      })
    );
  }

}
